/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreHandlerUtil - kCore's sort comparers, random / guid, logging and colour helpers.
 * Split out of kCoreHandler.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kCore keeps every member as a same-signature delegating member.
 */

import { getCoreUtils } from "./kCoreErrorRegistry";
import type { kCore } from "./kCoreHandler";
import { nz, roundToTwo } from "./kCoreHandlerValues";

export class kCoreHandlerUtil {
    // ── Sort comparators ───────────────────────────────────────────────────────

    /**
     * Compare two strings the way a PERSON orders a list: case-insensitively, and with runs of digits
     * read as NUMBERS rather than as characters.
     *
     * ⚠ WHY THIS EXISTS. Every comparator below used raw `<` / `>` on lowercased strings, which sorts
     * character by character — so `"Area 10"` came BEFORE `"Area 9"`, because `'1' < '9'`. The estate
     * had no natural comparator at all: a grep for `numeric: true` across kReusableCoreTS and
     * ReUsableTS returned zero non-test hits, while ONE consumer (MOSE) had hand-written the correct
     * rule at TWELVE separate call sites. That is the inverse of the usual duplication: the copies were
     * right and the shared original was wrong, so the fix belongs HERE (constitution rule 2 — fix in
     * the shared code, never work around it in a project).
     *
     * ⚠ EXACTLY ONE THING CHANGES: the ordering of digits. The comparators stay CASE-INSENSITIVE,
     * because that is what lower-casing both sides already gave every existing consumer, and altering
     * two behaviours in one edit would make any resulting complaint impossible to attribute.
     */
    // DOCUMENTED EXCEPTION: moved body of kCore.NaturalCompare - its gate, try/catch and handler label "kCore.NaturalCompare" stay verbatim, so error reports are unchanged.
    public static NaturalCompare = (Self: typeof kCore, a: string, b: string): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const A: string = nz(a).toLowerCase();
            const B: string = nz(b).toLowerCase();
            return A.localeCompare(B, undefined, { numeric: true });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kCore.NaturalCompare");
            return 0;
        }
    };

    public static SortObjectsByName = (Self: typeof kCore, a: any, b: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            if (typeof a.Name !== "undefined") { return Self.NaturalCompare(a.Name, b.Name); }
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil.SortObjectsByName"); return 0; }
    };

    public static SortObjectsByNumber = (Self: typeof kCore, a: any, b: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            if (a.Number < b.Number) { return -1; }
            if (a.Number > b.Number) { return 1; }
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil.SortObjectsByNumber"); return 0; }
    };

    public static SortObjectsByTitle = (Self: typeof kCore, a: any, b: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            if (Self.KNotNullOrUndefined(a.Title) && Self.KNotNullOrUndefined(b.Title)) {
                if (typeof a.Title !== "undefined") { return Self.NaturalCompare(a.Title, b.Title); }
                return 0;
            }
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil.SortObjectsByTitle"); return 0; }
    };

    public static SortObjectsBylabel = (Self: typeof kCore, a: any, b: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            if (typeof a.label !== "undefined") { return Self.NaturalCompare(a.label, b.label); }
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil.SortObjectsBylabel"); return 0; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.SortDescObjectByID - its gate, try/catch and handler label "kCore.SortDescObjectByID" stay verbatim, so error reports are unchanged.
    public static SortDescObjectByID = (Self: typeof kCore, a: any, b: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const aName: string = a.ID;
            const bName: string = b.ID;
            if (aName > bName) { return -1; }
            if (aName < bName) { return 1; }
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.SortDescObjectByID"); return 0; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.SortAscObjectByID - its gate, try/catch and handler label "kCore.SortAscObjectByID" stay verbatim, so error reports are unchanged.
    public static SortAscObjectByID = (Self: typeof kCore, a: any, b: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const aName: any = a.ID;
            const bName: any = b.ID;
            if (aName > bName) { return 1; }
            if (aName < bName) { return -1; }
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.SortAscObjectByID"); return 0; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.SortObjectById - its gate, try/catch and handler label "kCore.SortObjectById" stay verbatim, so error reports are unchanged.
    public static SortObjectById = (Self: typeof kCore, a: any, b: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const aName: any = a.id;
            const bName: any = b.id;
            if (aName < bName) { return -1; }
            if (aName > bName) { return 1; }
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.SortObjectById"); return 0; }
    };

    // ── Numbers / misc ─────────────────────────────────────────────────────────
    // DOCUMENTED EXCEPTION: moved body of kCore.RoundToTwo - its gate, try/catch and handler label "kCore.RoundToTwo" stay verbatim, so error reports are unchanged.
    public static RoundToTwo = (Self: typeof kCore, num: number): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try { return roundToTwo(num); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.RoundToTwo"); return 0; }
    };

    /** Fixed-width lower-case hex for collision guards, never credentials. Uses native random bytes
     * where available; Math.random fallback supports older/non-secure browser contexts.
     * DOCUMENTED EXCEPTION: like NewGuid, a latched gate must not turn an ID into an empty value.
     * SecureOnly=true propagates native failures to the caller and never uses weaker fallback randomness.
     * Invalid byte counts are defined refusals; allocation is capped at the platform's 65536-byte limit. */
    public static RandomHex = (Self: typeof kCore, ByteCount: number, SecureOnly: boolean = false): string => {
        if (Number.isInteger(ByteCount) === false || ByteCount < 1 || ByteCount > 65536) { return ""; }
        const Bytes: Uint8Array = new Uint8Array(ByteCount);
        let Native: boolean = false;
        try {
            const Host: Record<string, unknown> = globalThis as unknown as Record<string, unknown>;
            const Crypto: unknown = Host["crypto"];
            if (Crypto !== null && typeof Crypto === "object") {
                const Maker: unknown = (Crypto as Record<string, unknown>)["getRandomValues"];
                if (typeof Maker === "function") {
                    (Maker as (Value: Uint8Array) => Uint8Array).call(Crypto, Bytes);
                    Native = true;
                }
            }
        } catch (e) {
            if (SecureOnly === true) { throw e; }
            getCoreUtils().KGlobalErrorHandler(e, "kCore.RandomHex");
        }
        if (Native === false && SecureOnly === true) { throw new Error("Native random generation is unavailable."); }
        if (Native === false) {
            for (let At: number = 0; At < Bytes.length; At = At + 1) { Bytes[At] = Math.floor(Math.random() * 256); }
        }
        let Out: string = "";
        for (const Byte of Bytes) { Out = Out + ("0" + Byte.toString(16)).slice(-2); }
        return Out;
    };

    /**
     * A version-4 GUID. Uses the host's `crypto.randomUUID()` when it exists (browser secure contexts,
     * Node 14.17+), which is cryptographically random; otherwise the `Math.random` template, exactly
     * as before (SharePoint 2013 / old SPFx pages have no randomUUID, and a non-secure http page does
     * not either). The shape is the same either way: lower-case 8-4-4-4-12 hex, version digit 4,
     * variant digit 8-b. Lifted from DeckBuilder's DECKBSPVideoStorage.NewGuid (2026-10-05, D6).
     *
     * DOCUMENTED EXCEPTION to the KErrorMode guard: a pure function with a safe fallback. A guard that
     * returned "" would turn a latched gate into an empty id for a caller that is only building one,
     * and this method has never had one - it must keep answering.
     */
    public static NewGuid = (Self: typeof kCore): string => {
        try {
            const Native: string = Self["_NativeGuid"]();
            if (Native !== "") { return Native; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kCore.NewGuid");
        }
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, Self["_NewGuidReplacer"]);
    };

    /** The host's own GUID, or "" when the host has none (or it answered something that is not a GUID). */
    public static _NativeGuid = (Self: typeof kCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Host: Record<string, unknown> = globalThis as unknown as Record<string, unknown>;
            const Crypto: unknown = Host["crypto"];
            if (Crypto === null || typeof Crypto !== "object") { return ""; }
            const Maker: unknown = (Crypto as Record<string, unknown>)["randomUUID"];
            if (typeof Maker !== "function") { return ""; }
            const Made: unknown = (Maker as () => unknown).call(Crypto);
            if (typeof Made === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(Made)) { return Made; }
            return "";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil._NativeGuid"); return ""; }
    };

    // DOCUMENTED EXCEPTION: pure replace() callback of NewGuid (Math.random arithmetic only) - nothing in it can throw.
    public static _NewGuidReplacer = (Self: typeof kCore, c: string): string => {
        const r: number = (Math.random() * 16) | 0;
        let v: number = r;
        if (c !== "x") { v = (r & 0x3) | 0x8; }
        return v.toString(16);
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.AddOffSet - its gate, try/catch and handler label "kCore.AddOffSet" stay verbatim, so error reports are unchanged.
    public static AddOffSet = (Self: typeof kCore, Currdate: Date): Date | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            return new Date(Currdate.getTime() + -1 * Currdate.getTimezoneOffset() * 60000);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.AddOffSet"); return null; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.ColorLog - its gate, try/catch and handler label "kCore.ColorLog" stay verbatim, so error reports are unchanged.
    public static ColorLog = (Self: typeof kCore, message: string, color: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            color = color || "black";
            switch (color) {
                case "success": color = "Green"; break;
                case "info": color = "DodgerBlue"; break;
                case "error": color = "Red"; break;
                case "warning": color = "Orange"; break;
                default:
            }
            console.log("%c" + message, "color:" + color);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.ColorLog"); }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.ThrowError - its gate, try/catch and handler label "kCore.ThrowError" stay verbatim, so error reports are unchanged.
    public static ThrowError = (Self: typeof kCore): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const json = "{ bad json }";
            const user: object = JSON.parse(json);
            console.log(json);
            console.log(user);
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.ThrowError"); return 0; }
    };

    // ── Colour conversion ──────────────────────────────────────────────────────
    public static GetNextColor = (Self: typeof kCore, ArrayWithColors: any[]): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Item: any = null;
            let CurrColor = "";
            let StColor = "";
            let intUsedColors = 0;
            let CountItems = 0;
            let CountColors = 0;
            for (CountItems = 0; CountItems < ArrayWithColors.length; CountItems++) {
                Item = ArrayWithColors[CountItems];
                CurrColor = Item.Color;
                CurrColor = CurrColor.replace("rgb(", "");
                CurrColor = CurrColor.replace(")", "");
                if (CurrColor !== "") {
                    CurrColor = Self.ReplaceAll(", ", ",", CurrColor);
                    for (CountColors = 0; CountColors < Self.StandardColors.length; CountColors++) {
                        StColor = Self.StandardColors[CountColors];
                        if (CurrColor === StColor) {
                            if (CountColors > intUsedColors) { intUsedColors = CountColors; }
                        }
                    }
                }
            }
            if (intUsedColors < Self.StandardColors.length - 1) {
                return Self.StandardColors[intUsedColors + 1];
            }
            return Math.floor(Math.random() * 255) + "," + Math.floor(Math.random() * 255) + "," + Math.floor(Math.random() * 255);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil.GetNextColor"); return ""; }
    };

    private static readonly HexPalette: string[] = ["#CFCFC4","#AECACF","#F5F5F5","#FFDFC9","#FFEFD5","#FFDEAD","#EBFFF5","#B6C8E0","#D2BEBE","#C4C3D0","#A9BA9D","#ABCDEF","#FFF5FF"];

    public static GetNextHexColor = (Self: typeof kCore, ArrayWithColors: any[]): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Item: any = null;
            let CurrColor = "";
            let StColor = "";
            let intUsedColors = -1;
            const StandardColors: string[] = kCoreHandlerUtil.HexPalette;
            let CountItems = 0;
            let CountColors = 0;
            for (CountItems = 0; CountItems < ArrayWithColors.length; CountItems++) {
                Item = ArrayWithColors[CountItems];
                CurrColor = Self.Nz(Item.Color);
                if (CurrColor !== "") {
                    CurrColor = Self.ReplaceAll(", ", ",", CurrColor);
                    CurrColor = CurrColor.toUpperCase();
                    for (CountColors = 0; CountColors < StandardColors.length; CountColors++) {
                        StColor = StandardColors[CountColors];
                        if (CurrColor === StColor) {
                            if (CountColors > intUsedColors) { intUsedColors = CountColors; }
                        }
                    }
                }
            }
            if (intUsedColors < StandardColors.length - 1) {
                return StandardColors[intUsedColors + 1];
            }
            return "#" + ((Math.random() * 0xffffff) << 0).toString(16);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil.GetNextHexColor"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.GetRGBCodeFromHex - its gate, try/catch and handler label "kCore.GetRGBCodeFromHex" stay verbatim, so error reports are unchanged.
    public static GetRGBCodeFromHex = (Self: typeof kCore, HexColorCode: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let RGB: string = Self.HexToR(HexColorCode).toString();
            RGB = RGB + "," + Self.HexToG(HexColorCode).toString();
            RGB = RGB + "," + Self.HexToB(HexColorCode).toString();
            return RGB;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.GetRGBCodeFromHex"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.GetRGB - its gate, try/catch and handler label "kCore.GetRGB" stay verbatim, so error reports are unchanged.
    public static GetRGB = (Self: typeof kCore, color: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const R: number = Self.HexToR(color);
            const G: number = Self.HexToG(color);
            const B: number = Self.HexToB(color);
            return Self.GetRawColor(R, G, B);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.GetRGB"); return ""; }
    };

    public static CutHex = (Self: typeof kCore, h: string): string => {
        if (getCoreUtils().KErrorMode) { return h; }
        try {
            if (h.charAt(0) === "#") { return h.substring(1, 7); }
            return h;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil.CutHex"); return h; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.GetRawColor - its gate, try/catch and handler label "kCore.GetRawColor" stay verbatim, so error reports are unchanged.
    public static GetRawColor = (Self: typeof kCore, R: number, G: number, B: number): any => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let rgb: number = R;
            rgb = (rgb << 8) + G;
            rgb = (rgb << 8) + B;
            return rgb;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.GetRawColor"); }
    };

    public static SetCaptionColorStyle = (Self: typeof kCore, ColorCode: string, Important?: boolean): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let r = 0, g = 0, b = 0, Parts: string[] = [], strColor = "", yiq = 0;
            let NewColorCode: string = ColorCode;
            if (ColorCode.indexOf("#") > -1) {
                NewColorCode = Self.GetRGBCodeFromHex(ColorCode);
            } else {
                if (ColorCode.indexOf("rgb") > -1) {
                    NewColorCode = ColorCode.replace("rgb(", "").replace(")", "");
                }
            }
            Parts = NewColorCode.split(",");
            r = parseInt(Parts[0], 10);
            g = parseInt(Parts[1], 10);
            b = parseInt(Parts[2], 10);
            yiq = (r * 299 + g * 587 + b * 114) / 1000;
            if (yiq >= 128) { strColor = "black"; } else { strColor = "white"; }
            if (Important !== false) { strColor += "!important"; }
            return strColor;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerUtil.SetCaptionColorStyle"); return ""; }
    };
}
